import { z } from 'zod/v4';
import {
  ITriasProvisionedCallAtLocationStructure,
  ITriasBookingPTLegStructure,
  ITriasGeoPositionStructure,
  ITriasInternationalTextStructure,
  ITriasLocationRefStructure,
  ITriasServiceCallStructure,
  ITriasServiceJourneyPropertiesStructure,
  ITriasModeStructure,
  ITriasServiceViaPointStructure,
  ITriasGeneralAttributeStructure,
  ITriasAvailabilityResponse,
  ITriasAvailabilityResult,
  ITriasErrorMessageStructure,
  ITriasTicketStructure,
  ITriasFareZoneListInAreaStructure,
  ITriasFareZone,
  ITriasWebLinkStructure,
  ITriasBookableServiceInformationStructure,
  ITriasResponse,
  ITriasPayload,
  ITriasServiceDelivery,
  ITriasSiriString,
  ITriasPayloadBody,
  ITriasXmlDocumentWrapper,
  IRawTriasPayload,
  IXmlDocumentHeader,
  ITriasBookingResponse,
  ITriasBookingCancellationResponse,
} from './trias.interfaces';
import {
  SIRI_NAMESPACE,
  TRIAS_NAMESPACE,
  TRIAS_VERSION,
  XML_SCHEMA_2001,
  XML_SCHEMA_INSTANCE_2001,
} from './trias.constants';

export const TRIAS_GEO_POSITION_SCHEMA: z.ZodType<ITriasGeoPositionStructure> =
  z.object({
    Latitude: z.number(),
    Longitude: z.number(),
    Altitude: z.number().optional(),
  });

export const TRIAS_INTERNATIONAL_TEXT_SCHEMA: z.ZodType<ITriasInternationalTextStructure> =
  z.object({
    Text: z.string(),
    TextId: z.string().optional(),
    Language: z.string().optional(),
  });

export const TRIAS_LOCATION_REF_SCHEMA: z.ZodType<ITriasLocationRefStructure> =
  z.object({
    StopPointRef: z.string().optional(),
    StopPlaceRef: z.string().optional(),
    GeoPosition: TRIAS_GEO_POSITION_SCHEMA.optional(),
    LocationName: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA),
  });

export const TRIAS_SERVICE_CALL_SCHEMA: z.ZodType<ITriasServiceCallStructure> =
  z.object({
    TimetabledTime: z.iso.datetime({ offset: true }),
    RecordedAtTime: z.iso.datetime({ offset: true }).optional(),
    EstimatedTime: z.iso.datetime({ offset: true }).optional(),
    EstimatedTimeLow: z.iso.datetime({ offset: true }).optional(),
    EstimatedTimeHigh: z.iso.datetime({ offset: true }).optional(),
  });

export const TRIAS_PROVISIONED_CALL_AT_LOCATION_SCHEMA: z.ZodType<ITriasProvisionedCallAtLocationStructure> =
  z.object({
    JourneyRef: z.string().optional(),
    OperatingDayRef: z.string().optional(),
    CallLocation: TRIAS_LOCATION_REF_SCHEMA,
    ServiceArrival: TRIAS_SERVICE_CALL_SCHEMA.optional(),
    ServiceDeparture: TRIAS_SERVICE_CALL_SCHEMA.optional(),
    StopSeqNumber: z.int().positive().optional(),
    DemandStop: z.boolean().optional(),
    UnplannedStop: z.boolean().optional(),
    NotServicedStop: z.boolean().optional(),
    NoBoardingAtStop: z.boolean().optional(),
    NoAlightingAtStop: z.boolean().optional(),
  });

export const TRIAS_MODE_SCHEMA: z.ZodType<ITriasModeStructure> = z.object({
  PtMode: z.string(),
  BusSubmode: z.string().optional(),
  TramSubmode: z.string().optional(),
  RailSubmode: z.string().optional(),
  WaterSubmode: z.string().optional(),
  AirSubmode: z.string().optional(),
  TelecabinSubmode: z.string().optional(),
  FunicularSubmode: z.string().optional(),
  TaxiSubmode: z.string().optional(),
  Name: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  ShortName: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  DestinationText: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
});

export const TRIAS_SERVICE_VIA_POINT_SCHEMA: z.ZodType<ITriasServiceViaPointStructure> =
  z.object({
    StopPointRef: z.string(),
    StopPointName: TRIAS_INTERNATIONAL_TEXT_SCHEMA,
    NameSuffix: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
    PlannedBay: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
    EstimatedBay: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
    DisplayPriority: z.int().min(1).max(5).optional(),
  });

export const TRIAS_GENERAL_ATTRIBUTE_SCHEMA: z.ZodType<ITriasGeneralAttributeStructure> =
  z.object({
    Text: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA),
    Code: z.string(),
  });

export const TRIAS_SERVICE_JOURNEY_PROPERTIES: z.ZodType<ITriasServiceJourneyPropertiesStructure> =
  z.object({
    JourneyRef: z.string().optional(),
    LineRef: z.string().optional(),
    DirectionRef: z.string().optional(),
    Mode: TRIAS_MODE_SCHEMA.optional(),
    PublishedLineName: TRIAS_INTERNATIONAL_TEXT_SCHEMA.optional(),
    OperatorRef: z.string().optional(),
    RouteDescription: TRIAS_INTERNATIONAL_TEXT_SCHEMA.optional(),
    Via: z.array(TRIAS_SERVICE_VIA_POINT_SCHEMA).optional(),
    Attribute: z.array(TRIAS_GENERAL_ATTRIBUTE_SCHEMA).optional(),
  });

export const TRIAS_BOOKING_PT_LEG_SCHEMA: z.ZodType<ITriasBookingPTLegStructure> =
  z.object({
    PickUpLocation: TRIAS_PROVISIONED_CALL_AT_LOCATION_SCHEMA,
    SetDownLocation: TRIAS_PROVISIONED_CALL_AT_LOCATION_SCHEMA,
    Service: TRIAS_SERVICE_JOURNEY_PROPERTIES.optional(),
  });

export const TRIAS_ERROR_MESSAGE_SCHEMA: z.ZodType<ITriasErrorMessageStructure> =
  z.object({
    Code: z.string(),
    Text: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  });

export const TRIAS_FARE_ZONE_SCHEMA: z.ZodType<ITriasFareZone> = z.object({
  FareZoneRef: z.string(),
  FareZoneText: z.string(),
});

export const TRIAS_FARE_ZONE_LIST_IN_AREA_SCHEMA: z.ZodType<ITriasFareZoneListInAreaStructure> =
  z.object({
    FaresAuthorityRef: z.string(),
    FaresAuthorityText: z.string(),
    FareZone: z.array(TRIAS_FARE_ZONE_SCHEMA),
  });

export const TRIAS_WEB_LINK_SCHEMA: z.ZodType<ITriasWebLinkStructure> =
  z.object({
    Label: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA),
    Url: z.url(),
  });

export const TRIAS_TICKET_SCHEMA: z.ZodType<ITriasTicketStructure> = z.object({
  TicketId: z.string(),
  TicketName: z.string(),
  FaresAuthorityRef: z.string(),
  FaresAuthorityText: z.string(),
  Price: z.number().optional(),
  NetPrice: z.number().optional(),
  Currency: z.string().optional(),
  VatRate: z.string().optional(),
  TariffLevel: z.string().optional(),
  TariffLevelLabel: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  TravelClass: z.string().optional(),
  RequiredCard: z.array(z.string()).optional(),
  ValidFor: z.array(z.string()).optional(),
  ValidityDuration: z.iso.duration().optional(),
  ValidityDurationText: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  ValidityFareZones: TRIAS_FARE_ZONE_LIST_IN_AREA_SCHEMA.optional(),
  ValidityAreaText: z.array(TRIAS_INTERNATIONAL_TEXT_SCHEMA).optional(),
  UsageValidityType: z.string().optional(),
  InfoUrl: z.array(TRIAS_WEB_LINK_SCHEMA).optional(),
  SaleUrl: z.array(TRIAS_WEB_LINK_SCHEMA).optional(),
  BookingInfo: z.array(TRIAS_WEB_LINK_SCHEMA).optional(),
});

export const TRIAS_BOOKABLE_SERVICE_INFORMATION_SCHEMA: z.ZodType<ITriasBookableServiceInformationStructure> =
  z.object({
    WheelchairCapacity: z.number().min(0).optional(),
    MotorizedWheelchairCapacity: z.number().min(0).optional(),
    FoldableWheelchairCapacity: z.number().min(0).optional(),
    WalkingFrameCapacity: z.number().min(0).optional(),
    PramCapacity: z.number().min(0).optional(),
    HeavyLuggageCapacity: z.number().min(0).optional(),
    ChildSeatCapacity: z.number().min(0).optional(),
    ChildSeatSupply: z.number().min(0).optional(),
    BabyCarrierCapacity: z.number().min(0).optional(),
    BabyCarrierSupply: z.number().min(0).optional(),
    SeatBoosterCapacity: z.number().min(0).optional(),
    SeatBoosterSupply: z.number().min(0).optional(),
    BikeCapacity: z.number().min(0).optional(),
    GuideDogCapacity: z.number().min(0).optional(),
    DogCapacity: z.number().min(0).optional(),
  });

// region Availability

// region Availability Types

const TRIAS_AVAILABILITY_RESULT_SCHEMA: z.ZodType<ITriasAvailabilityResult> =
  z.object({
    ErrorMessage: z.array(TRIAS_ERROR_MESSAGE_SCHEMA).optional(),
    PublicTransport: TRIAS_BOOKING_PT_LEG_SCHEMA.optional(),
    BookableTicket: z.array(TRIAS_TICKET_SCHEMA).optional(),
    BookableServiceInformation:
      TRIAS_BOOKABLE_SERVICE_INFORMATION_SCHEMA.optional(),
  });

// endregion

export const TRIAS_AVAILABILITY_RESPONSE_SCHEMA: z.ZodType<
  IRawTriasPayload<ITriasAvailabilityResponse>
> = z.object({
  AvailabilityResult: z.array(TRIAS_AVAILABILITY_RESULT_SCHEMA).optional(),
  ErrorMessage: z.array(TRIAS_ERROR_MESSAGE_SCHEMA).optional(),
});

// endregion

// region Booking

export const TRIAS_BOOKING_RESPONSE_SCHEMA: z.ZodType<
  IRawTriasPayload<ITriasBookingResponse>
> = z.object({
  ErrorMessage: z.array(TRIAS_ERROR_MESSAGE_SCHEMA).optional(),
  SystemRef: z.string().optional(),
  BookingCode: z.string().optional(),
  PublicTransport: TRIAS_BOOKING_PT_LEG_SCHEMA.optional(),
  ExpiresAfter: z.string().optional(),
  ChangeableUntil: z.string().optional(),
  CancellableUntil: z.string().optional(),
});

export const TRIAS_BOOKING_CANCELLATION_RESPONSE_SCHEMA: z.ZodType<
  IRawTriasPayload<ITriasBookingCancellationResponse>
> = z.object({
  ErrorMessage: z.array(TRIAS_ERROR_MESSAGE_SCHEMA).optional(),
});

// endregion

// region Request Wrapper & Internals

const SIRI_STRING_SCHEMA = (
  validation: z.ZodStringFormat | z.ZodString
): z.ZodType<ITriasSiriString> => {
  return z.object({
    '@_xmlns': z.literal(SIRI_NAMESPACE),
    '#text': validation,
  });
};

export const TRIAS_SIRI_STRING_SCHEMA: z.ZodType<ITriasSiriString> =
  SIRI_STRING_SCHEMA(z.string());

function PAYLOAD_BODY<TPayload extends ITriasPayload>(
  payloadName: TPayload['_payloadName'],
  payloadSchema: z.ZodType<IRawTriasPayload<TPayload>>
): z.ZodType<ITriasPayloadBody<TPayload>> {
  // @ts-expect-error payloadName is type safe but TypeScript does not detect that
  return z.object({
    [payloadName]: payloadSchema,
  });
}

function SERVICE_DELIVERY_SCHEMA<TPayload extends ITriasPayload>(
  payloadName: TPayload['_payloadName'],
  payloadSchema: z.ZodType<IRawTriasPayload<TPayload>>
): z.ZodType<ITriasServiceDelivery<TPayload>> {
  return z.object({
    ResponseTimestamp: SIRI_STRING_SCHEMA(z.iso.datetime({ offset: true })),
    ProducerRef: TRIAS_SIRI_STRING_SCHEMA,
    DeliveryPayload: PAYLOAD_BODY(payloadName, payloadSchema),
  });
}

const XML_HEADER_SCHEMA: z.ZodType<IXmlDocumentHeader> = z.object({
  '@_version': z.literal('1.0'),
  '@_encoding': z.literal('UTF-8'),
  '@_standalone': z.string().optional(),
});

export function TRIAS_RESPONSE_SCHEMA<TPayload extends ITriasPayload>(
  payloadName: TPayload['_payloadName'],
  payloadSchema: z.ZodType<IRawTriasPayload<TPayload>>
): z.ZodType<ITriasXmlDocumentWrapper<ITriasResponse<TPayload>>> {
  return z.intersection(
    z.object({
      '?xml': XML_HEADER_SCHEMA,
    }),
    z.union([
      z.object({
        Trias: z.object({
          '@_xmlns': z.literal(TRIAS_NAMESPACE),
          '@_xmlns:xsd': z.literal(XML_SCHEMA_2001),
          '@_xmlns:xsi': z.literal(XML_SCHEMA_INSTANCE_2001),
          '@_version': z.literal(TRIAS_VERSION),
          ServiceDelivery: SERVICE_DELIVERY_SCHEMA(payloadName, payloadSchema),
        }),
      }),
      z.intersection(
        PAYLOAD_BODY(payloadName, payloadSchema),
        z.object({
          [payloadName]: z.object({
            '@_xmlns': z.literal(TRIAS_NAMESPACE),
          }),
        })
      ),
    ])
  );
}

// endregion
